<template>
  <div class="admin-dashboard">
    <!-- 页面标题 -->
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="page-title">
            <i class="bi bi-speedometer2 me-2"></i>
            管理中心仪表盘
          </h1>
          <p class="page-subtitle text-muted mb-0">
            欢迎回来，{{ authStore.user?.username }}！今天是 {{ formatDate(new Date()) }}
          </p>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="row mb-4">
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="stats-card stats-primary">
          <div class="stats-icon">
            <i class="bi bi-collection"></i>
          </div>
          <div class="stats-content">
            <div class="stats-number">{{ stats.totalQuestionBanks }}</div>
            <div class="stats-label">题库总数</div>
          </div>
        </div>
      </div>
      
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="stats-card stats-success">
          <div class="stats-icon">
            <i class="bi bi-question-circle"></i>
          </div>
          <div class="stats-content">
            <div class="stats-number">{{ stats.totalQuestions }}</div>
            <div class="stats-label">题目总数</div>
          </div>
        </div>
      </div>
      
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="stats-card stats-info">
          <div class="stats-icon">
            <i class="bi bi-people"></i>
          </div>
          <div class="stats-content">
            <div class="stats-number">{{ stats.totalUsers }}</div>
            <div class="stats-label">用户总数</div>
          </div>
        </div>
      </div>
      
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="stats-card stats-warning">
          <div class="stats-icon">
            <i class="bi bi-graph-up"></i>
          </div>
          <div class="stats-content">
            <div class="stats-number">{{ stats.totalSessions }}</div>
            <div class="stats-label">答题会话</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 快速操作 -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="quick-actions-card">
          <h5 class="card-title mb-3">
            <i class="bi bi-lightning me-2"></i>
            快速操作
          </h5>
          <div class="row">
            <div class="col-lg-3 col-md-6 mb-3">
              <router-link to="/admin/question-banks/new" class="quick-action-btn">
                <div class="action-icon bg-primary">
                  <i class="bi bi-plus-circle"></i>
                </div>
                <div class="action-content">
                  <h6>创建题库</h6>
                  <p>添加新的题库</p>
                </div>
              </router-link>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
              <router-link to="/admin/question-banks/import" class="quick-action-btn">
                <div class="action-icon bg-success">
                  <i class="bi bi-cloud-upload"></i>
                </div>
                <div class="action-content">
                  <h6>导入题库</h6>
                  <p>批量导入题目</p>
                </div>
              </router-link>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
              <router-link to="/admin/users" class="quick-action-btn">
                <div class="action-icon bg-info">
                  <i class="bi bi-people"></i>
                </div>
                <div class="action-content">
                  <h6>用户管理</h6>
                  <p>管理系统用户</p>
                </div>
              </router-link>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
              <router-link to="/admin/roles" class="quick-action-btn">
                <div class="action-icon bg-warning">
                  <i class="bi bi-person-badge"></i>
                </div>
                <div class="action-content">
                  <h6>角色管理</h6>
                  <p>配置用户权限</p>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 最近活动 -->
    <div class="row">
      <div class="col-lg-8 mb-4">
        <div class="activity-card">
          <h5 class="card-title mb-3">
            <i class="bi bi-clock-history me-2"></i>
            最近活动
          </h5>
          <div class="activity-list">
            <div class="activity-item" v-for="activity in recentActivities" :key="activity.id">
              <div class="activity-icon" :class="activity.iconClass">
                <i :class="activity.icon"></i>
              </div>
              <div class="activity-content">
                <p class="activity-text">{{ activity.text }}</p>
                <small class="activity-time text-muted">{{ activity.time }}</small>
              </div>
            </div>
            
            <div v-if="recentActivities.length === 0" class="text-center py-4 text-muted">
              <i class="bi bi-inbox display-4 mb-3"></i>
              <p>暂无最近活动</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-4 mb-4">
        <div class="system-info-card">
          <h5 class="card-title mb-3">
            <i class="bi bi-info-circle me-2"></i>
            系统信息
          </h5>
          <div class="info-list">
            <div class="info-item">
              <span class="info-label">系统版本</span>
              <span class="info-value">v1.0.0</span>
            </div>
            <div class="info-item">
              <span class="info-label">运行时间</span>
              <span class="info-value">{{ systemUptime }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">数据库状态</span>
              <span class="info-value text-success">
                <i class="bi bi-check-circle me-1"></i>
                正常
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">最后备份</span>
              <span class="info-value">{{ lastBackupTime }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { adminApi, type SystemStats, type RecentActivity } from '../../api/admin'

const authStore = useAuthStore()

// 统计数据
const stats = ref<SystemStats>({
  totalQuestionBanks: 0,
  totalQuestions: 0,
  totalUsers: 0,
  totalSessions: 0,
  completedSessions: 0,
  activeUsers: 0
})

// 最近活动
const recentActivities = ref<Array<RecentActivity & { text: string; time: string; icon: string; iconClass: string }>>([])

// 系统信息
const systemUptime = ref('3天 12小时')
const lastBackupTime = ref('昨天 23:30')

// 格式化日期
const formatDate = (date: Date) => {
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  })
}

// 格式化时间差
const formatTimeAgo = (dateString: string) => {
  const now = new Date()
  const date = new Date(dateString)
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
  
  if (diffInMinutes < 1) return '刚刚'
  if (diffInMinutes < 60) return `${diffInMinutes}分钟前`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}小时前`
  return `${Math.floor(diffInMinutes / 1440)}天前`
}

// 加载统计数据
const loadStats = async () => {
  try {
    const systemStats = await adminApi.getSystemStats()
    stats.value = systemStats
  } catch (error) {
    console.error('加载统计数据失败:', error)
  }
}

// 加载最近活动
const loadRecentActivities = async () => {
  try {
    const activities = await adminApi.getRecentActivities()
    recentActivities.value = activities.map(activity => {
      let text = ''
      let icon = 'bi bi-activity'
      let iconClass = 'bg-secondary'
      
      if (activity.type === 'quiz_session') {
        if (activity.status === 'completed') {
          text = `用户 ${activity.user.firstName || activity.user.username} 完成了《${activity.questionBank.subdomain}》答题`
          icon = 'bi bi-person-check'
          iconClass = 'bg-success'
        } else {
          text = `用户 ${activity.user.firstName || activity.user.username} 开始了《${activity.questionBank.subdomain}》答题`
          icon = 'bi bi-play-circle'
          iconClass = 'bg-primary'
        }
      }
      
      return {
        ...activity,
        text,
        time: formatTimeAgo(activity.startedAt),
        icon,
        iconClass
      }
    })
  } catch (error) {
    console.error('加载最近活动失败:', error)
  }
}

onMounted(() => {
  loadStats()
  loadRecentActivities()
})
</script>

<style scoped>
.admin-dashboard {
  padding: 0;
}

.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  border-radius: 15px;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
}

/* 统计卡片样式 */
.stats-card {
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: none;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  height: 100%;
}

.stats-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.stats-icon {
  width: 60px;
  height: 60px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  margin-right: 1rem;
}

.stats-primary .stats-icon {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.stats-success .stats-icon {
  background: linear-gradient(135deg, #56ab2f, #a8e6cf);
}

.stats-info .stats-icon {
  background: linear-gradient(135deg, #3498db, #2980b9);
}

.stats-warning .stats-icon {
  background: linear-gradient(135deg, #f39c12, #e67e22);
}

.stats-number {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
}

.stats-label {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin-top: 0.25rem;
}

/* 快速操作卡片 */
.quick-actions-card {
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.card-title {
  color: #2c3e50;
  font-weight: 600;
}

.quick-action-btn {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: white;
  border: 2px solid #f8f9fa;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  height: 100%;
}

.quick-action-btn:hover {
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
  color: inherit;
  text-decoration: none;
}

.action-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
}

.action-content h6 {
  margin-bottom: 0.25rem;
  font-weight: 600;
  color: #2c3e50;
}

.action-content p {
  margin: 0;
  font-size: 0.85rem;
  color: #7f8c8d;
}

/* 活动卡片 */
.activity-card,
.system-info-card {
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.activity-item {
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f8f9fa;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-item .activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
}

.activity-text {
  margin: 0;
  font-weight: 500;
  color: #2c3e50;
}

.activity-time {
  font-size: 0.8rem;
}

/* 系统信息 */
.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f8f9fa;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 500;
  color: #7f8c8d;
}

.info-value {
  font-weight: 600;
  color: #2c3e50;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    padding: 1.5rem;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .stats-card {
    margin-bottom: 1rem;
  }
  
  .quick-actions-card,
  .activity-card,
  .system-info-card {
    padding: 1.5rem;
  }
}
</style>